import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { API_ROUTES } from '../config/api';

export interface ModelStatusEntry {
  training_status: string;
  training_task_id: string | null;
  prediction_status: string | null;
  prediction_id: number | null;
}

/** Effective status for display: training takes precedence over predicting */
export const getEffectiveStatus = (entry: ModelStatusEntry | null): string | null => {
  if (!entry) return null;
  if (entry.training_status === 'training') return 'training';
  if (entry.prediction_status === 'running' || entry.prediction_status === 'pending') return 'predicting';
  return entry.training_status;
};

export const useAllModelsStatus = (
  databaseId: number | string | null,
  pollInterval: number = 3000,
  forcePolling: boolean = false
) => {
  const [models, setModels] = useState<Record<string, ModelStatusEntry>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasActiveTask = Object.values(models).some(
    (m) =>
      m.training_status === 'training' ||
      m.prediction_status === 'running' ||
      m.prediction_status === 'pending'
  );

  const fetchStatus = useCallback(async () => {
    if (!databaseId) return;

    setIsLoading(true);
    try {
      const response = await api.get(
        `${API_ROUTES.PREDICTION.BASE}${API_ROUTES.PREDICTION.AVAILABLE_MODELS(databaseId)}`
      );
      const list = response.data || [];
      const modelsMap: Record<string, ModelStatusEntry> = {};
      for (const m of list) {
        const key = (m.id || m.name || '').toLowerCase();
        if (!key) continue;
        modelsMap[key] = {
          training_status: m.status || 'not_trained',
          training_task_id: m.training_task_id ?? null,
          prediction_status: m.prediction_status ?? null,
          prediction_id: m.prediction_id ?? null,
        };
      }
      setModels(modelsMap);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch status');
    } finally {
      setIsLoading(false);
    }
  }, [databaseId]);

  const shouldPoll = forcePolling || hasActiveTask;

  useEffect(() => {
    fetchStatus();

    let intervalId: ReturnType<typeof setInterval> | null = null;
    if (shouldPoll) {
      intervalId = setInterval(fetchStatus, pollInterval);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [fetchStatus, pollInterval, shouldPoll]);

  return {
    models,
    isLoading,
    error,
    refetch: fetchStatus,
    hasActiveTask,
    getStatus: (algorithm: string) => models[algorithm?.toLowerCase()] ?? null,
    getEffectiveStatus: (algorithm: string) =>
      getEffectiveStatus(models[algorithm?.toLowerCase()] ?? null),
  };
};
